import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { getPostBySlug, BLOG_POSTS } from "@/data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <div className="label-eyebrow">404</div>
        <h1 className="wordmark mt-4 text-4xl uppercase">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-primary hover:underline">
          <ArrowLeft size={16} /> Back to the blog
        </Link>
      </div>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);
  const publishedDate = new Date(post.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <article className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="blog-back">
        <ArrowLeft size={14} /> All Articles
      </Link>

      <div className="mt-8">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: post.accent || "var(--primary)" }} />
          <span>{post.category}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {post.read_minutes} min read</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Calendar size={11} /> {publishedDate}</span>
        </div>
        <h1 className="wordmark mt-4 text-4xl md:text-5xl uppercase leading-[1.02]" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="mt-6 text-lg text-foreground/80 leading-relaxed border-l-2 border-primary pl-4">{post.deck}</p>
      </div>

      {post.hero_image && (
        <div className="mt-10 rounded-lg overflow-hidden border border-border" data-testid="blog-hero-image">
          <img src={post.hero_image} alt={post.title} className="block w-full h-auto" />
        </div>
      )}

      <div className="mt-10 space-y-5" data-testid="blog-post-body">
        {post.body.map((block, i) => {
          if (block.type === "h2") return (
            <h2 key={i} className="wordmark uppercase text-2xl md:text-3xl pt-6 leading-tight" style={{ color: post.accent || undefined }}>
              {block.text}
            </h2>
          );
          if (block.type === "p") return (
            <p key={i} className="text-base text-foreground/85 leading-[1.75]">{block.text}</p>
          );
          if (block.type === "ul") return (
            <ul key={i} className="space-y-2.5 pl-1">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-3 text-base text-foreground/85 leading-relaxed">
                  <span className="mt-2.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: post.accent || "var(--primary)" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          );
          if (block.type === "quote") return (
            <blockquote key={i} className="my-8 p-6 bg-card border-l-4 rounded-r-md" style={{ borderColor: post.accent || "var(--primary)" }}>
              {block.attribution && (
                <div className="label-eyebrow mb-3" style={{ color: post.accent || undefined }}>{block.attribution}</div>
              )}
              <p className="text-lg font-head italic leading-relaxed text-foreground/90">{block.text}</p>
            </blockquote>
          );
          if (block.type === "refs") return (
            <div key={i} className="mt-10 pt-6 border-t border-border">
              <div className="label-eyebrow mb-4">References</div>
              <ol className="space-y-3 text-xs text-muted-foreground leading-relaxed">
                {block.items.map((ref) => (
                  <li key={ref.n} className="flex gap-3">
                    <span className="text-foreground font-semibold shrink-0">{ref.n}.</span>
                    <span>
                      {ref.text}{" "}
                      {ref.url && <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">{ref.url}</a>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          );
          return null;
        })}
      </div>

      {/* Related posts */}
      {related.length > 0 && (
        <div className="mt-16 pt-10 border-t border-border">
          <div className="label-eyebrow mb-6">Keep reading</div>
          <div className="grid md:grid-cols-2 gap-4">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`}
                className="group p-6 bg-card border border-border rounded-md hover:border-primary transition-colors">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.category}</div>
                <div className="mt-2 font-head uppercase text-lg leading-tight group-hover:text-primary transition-colors">{p.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="mt-16 rounded-lg bg-foreground text-background p-8 md:p-10 text-center">
        <div className="label-eyebrow !text-primary">The BA30 Program</div>
        <h3 className="wordmark mt-4 text-3xl md:text-4xl uppercase">Reading is a start.<br /><span className="text-primary">Building is what changes things.</span></h3>
        <Link to="/join" className="btn-primary mt-8 inline-block !text-base !py-3.5 !px-8" data-testid="blog-cta-join">
          Start Your 10K30 →
        </Link>
      </div>
    </article>
  );
}
